import React, { useCallback, useEffect, useState } from 'react';
import { CalendarPlus, MessageSquare, Phone, RefreshCw } from 'lucide-react';
import { CORE4, FORMQUICK_LOCKUP, MILLI_WORDMARK, daysAgo, delta, fetchDashboard, fmtMoney, fmtNumber, fmtWhen } from '@/lib/milliDashboardData';
import { fetchHistory } from '@/lib/milliData';
import TubesBackground from './TubesBackground';
import ConsoleBoard, { readBoardSettings } from './ConsoleBoard';
import { useAuth } from '@/context/AuthContext';

/* Milli's dashboard: the front door of /milli (2026-09-30).
 *
 * Built from the Superdesign concept J.O. approved (draft version 10) and
 * the rulebook v1.4: charcoal grounds (#1e2023 page, #25272B section,
 * #2c2e33 card, #3a3c42 border), K.O. antique gold #C9A24B as the one
 * primary accent, copper #C08A5E as Milli's second metal (trend values, the
 * rule under a section title, PQ items), brushed silver #b8b8b8 for
 * structure, cream #EDEBE6 text, Cinzel for display through .font-display,
 * Inter for everything else. Phone first: one column under 1024px.
 * Desk gets the Superdesign tube tracers behind the cards (TubesBackground,
 * a lazy chunk, off under prefers-reduced-motion) and the Milli wordmark
 * centered under the header at 6%; phones keep the static charcoal.
 *
 * Every number comes from kc_dashboard() (migration 085) in one round trip.
 * A block whose source is not wired yet says so ("connect Cloudflare
 * analytics") instead of showing an invented figure; that is the K.O. Rule
 * applied to our own dashboard.
 *
 * My board (2026-10-24, J.O.): every card is a function of one context
 * object so the same card can render twice, once in the fixed layout above
 * and once more on the board at the bottom, where J.O. picks the cards he
 * wants and the colors they wear (ConsoleBoard). In the fixed layout the
 * context carries the rulebook colors, so nothing above the board changed. */

const GOLD = '#C9A24B';
const COPPER = '#C08A5E';
// FormQuick's purple (rulebook v1.5, FormQuick identity).
const PURPLE = '#7B4FC0';
const SILVER = '#b8b8b8';
const MUTED = '#b0aca3';

const Label = ({ children, className = '' }) => (
  <div className={`text-[11px] font-semibold uppercase tracking-[0.14em] text-[#b0aca3] ${className}`}>{children}</div>
);

/* `board` swaps the fixed card colors for the board's CSS variables
 * (set on the board wrapper by ConsoleBoard); the fixed layout never
 * passes it, so those cards keep the rulebook hex values. */
const Card = ({ children, className = '', testId, board = false }) => (
  <section
    className={`rounded-[14px] border p-4 lg:p-5 shadow-[0_8px_24px_rgba(0,0,0,0.35)] ${board ? '' : 'border-[#3a3c42] bg-[#2c2e33]'} ${className}`}
    style={board ? { background: 'var(--board-card)', borderColor: 'var(--board-border)' } : undefined}
    data-testid={testId}
  >
    {children}
  </section>
);

const Rule = ({ color = COPPER }) => <div className="mt-2 mb-4 h-px w-12" style={{ background: color }} />;

const Trend = ({ value, suffix, color = COPPER }) => {
  if (value == null) return null;
  return (
    <div className="mt-1 text-[11px] font-semibold uppercase tracking-[0.1em]" style={{ color }}>
      {value} {suffix}
    </div>
  );
};

const Coming = ({ children }) => <div className="mt-1 text-[11px] text-[#8f8b83]">{children}</div>;

const Chip = ({ children, color = SILVER }) => (
  <span
    className="inline-block rounded-md border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em]"
    style={{ color, borderColor: `${color}80` }}
  >
    {children}
  </span>
);

const Stat = ({ label, value, trend, trendSuffix, gold, coming, testId, board, accent }) => (
  <Card testId={testId} board={board}>
    <Label>{label}</Label>
    <div className={`mt-2 text-[30px] leading-none font-extrabold ${gold ? '' : 'text-[#EDEBE6]'}`} style={gold ? { color: GOLD } : undefined}>
      {value == null ? <span className="text-[#6f6c66]">--</span> : value}
    </div>
    {value == null ? <Coming>{coming}</Coming> : <Trend value={trend} suffix={trendSuffix} color={accent} />}
  </Card>
);

const STAGES = [
  ['reached_out', 'reachedOut'],
  ['signed_up', 'signedUp'],
  ['demo_booked', 'demoBooked'],
  ['demo_done', 'demoDone'],
  ['paying', 'paying'],
];

const CHANNEL_LABEL = { facebook: 'Facebook', instagram: 'Instagram', blog: 'Blog', ads: 'Ads', referral: 'Referral', phone: 'Phone', direct: 'Direct' };

/* ---------------------------------------------------------------------------
 * The cards. Each takes the dashboard context `c` and returns the card it
 * always returned. `c.board` is true on My board, `c.accent` is the rule and
 * trend color (copper in the fixed layout, the board's accent on the board)
 * and `c.tid(id)` keeps test ids unique when a card shows twice on the page.
 * ------------------------------------------------------------------------- */

const cardVisitors = (c) => (
  <Stat label={c.tx('visitors')} value={fmtNumber(c.f.visitors_7d)} coming={c.tx('connectAnalytics')} testId={c.tid('dash-visitors')} board={c.board} accent={c.accent} />
);
const cardSignups = (c) => (
  <Stat label={c.tx('signups')} value={fmtNumber(c.f.signups_7d)} trend={delta(c.f.signups_7d, c.f.signups_prev_7d)} trendSuffix={c.tx('vsLastWeek')} testId={c.tid('dash-signups')} board={c.board} accent={c.accent} />
);
const cardDemos = (c) => (
  <Stat label={c.tx('demosBooked')} value={fmtNumber(c.f.demos_booked_7d)} trend={c.f.demos_upcoming != null ? `${c.f.demos_upcoming}` : null} trendSuffix={c.tx('upcoming')} testId={c.tid('dash-demos')} board={c.board} accent={c.accent} />
);
const cardRequests = (c) => (
  <Stat label={c.tx('demoRequests')} value={fmtNumber(c.f.demo_requests_7d)} trend={delta(c.f.demo_requests_7d, c.f.demo_requests_prev_7d)} trendSuffix={c.tx('vsLastWeek')} testId={c.tid('dash-requests')} board={c.board} accent={c.accent} />
);
const cardMrr = (c) => (
  <Stat label={c.tx('mrr')} value={c.f.mrr != null ? `$${fmtNumber(c.f.mrr)}` : null} gold coming={c.tx('connectStripe')} testId={c.tid('dash-mrr')} board={c.board} accent={c.accent} />
);

/* Trials (091): every signup is a trial until billing says otherwise. */
const cardTrials = (c) => {
  const { tx, trials, trialRows } = c;
  return (
    <Card testId={c.tid('milli-dashboard-trials')} board={c.board}>
      <div className="flex items-center justify-between">
        <Label>{tx('trials')}</Label>
        {trialRows.length ? (
          <span className="text-[11px] text-[#b0aca3]">
            {tx('trialsCount', { count: Number(trials.active || 0) + Number(trials.ending_soon || 0) })}
            {Number(trials.ending_soon) > 0 ? <span style={{ color: COPPER }}> · {tx('trialsEndingSoon', { count: Number(trials.ending_soon) })}</span> : null}
          </span>
        ) : null}
      </div>
      <Rule color={c.accent} />
      {trialRows.length === 0 ? (
        <p className="text-[13px] text-[#8f8b83]">{tx('trialsEmpty')}</p>
      ) : (
        <ul className="space-y-4">
          {trialRows.map((r) => {
            const total = r.started_at && r.trial_ends_at ? Math.max(1, (new Date(r.trial_ends_at) - new Date(r.started_at)) / 86400000) : 30;
            const left = r.days_left == null ? null : Number(r.days_left);
            const pct = left == null ? 0 : Math.max(0, Math.min(100, ((total - left) / total) * 100));
            const color = r.status === 'expired' ? '#8f8b83' : r.status === 'ending_soon' ? COPPER : GOLD;
            return (
              <li key={r.user_id} data-testid="milli-trial-row">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-[14px] font-bold text-[#EDEBE6]">{r.company || r.name}</div>
                    <div className="truncate text-[11px] uppercase tracking-[0.1em] text-[#b0aca3]">{[r.name !== r.company ? r.name : null, r.tier].filter(Boolean).join(', ')}</div>
                  </div>
                  <span className="shrink-0 text-[12px] font-bold" style={{ color }}>
                    {left == null ? tx('trialNone') : left >= 0 ? tx('trialDaysLeft', { count: left }) : tx('trialEnded', { count: Math.abs(left) })}
                  </span>
                </div>
                <div className="mt-2 h-1 w-full rounded-full bg-[#3a3c42]"><div className="h-1 rounded-full" style={{ width: `${pct}%`, background: color }} /></div>
                <div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-[11px] text-[#8f8b83]">
                  <span>{tx('trialSpan', { start: fmtWhen(r.started_at), end: r.trial_ends_at ? fmtWhen(r.trial_ends_at) : '--' })}</span>
                  <span className="flex gap-1">
                    {r.email_confirmed === false ? <Chip color={COPPER}>{tx('trialUnconfirmed')}</Chip> : null}
                    {r.onboarded === false ? <Chip>{tx('trialNotOnboarded')}</Chip> : null}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
};

const cardChannels = (c) => {
  const { tx, channels, winner } = c;
  return (
    <Card testId={c.tid('milli-dashboard-channels')} board={c.board}>
      <Label>{tx('channels')}</Label>
      <Rule color={c.accent} />
      {channels.length === 0 ? (
        <p className="text-[13px] text-[#8f8b83]">{tx('channelsEmpty')}</p>
      ) : (
        <ul className="space-y-1">
          {channels.map((ch) => {
            const win = winner && winner.channel === ch.channel && ch.leads > 0;
            return (
              <li
                key={ch.channel}
                className={`flex items-center justify-between rounded-md px-3 py-2 ${win ? 'border-l-2 bg-[rgba(201,162,75,0.08)]' : ''}`}
                style={win ? { borderColor: GOLD } : undefined}
              >
                <div>
                  <div className="text-[14px]" style={win ? { color: GOLD } : undefined}>{CHANNEL_LABEL[ch.channel] || ch.channel}</div>
                  {win ? <div className="text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: GOLD }}>{tx('winner')}</div> : null}
                </div>
                <div className="text-right">
                  <div className="text-[13px] text-[#EDEBE6]">{ch.leads} {tx('leads')}</div>
                  <div className="text-[11px] text-[#8f8b83]">{ch.posts} {tx('posts')}</div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
};

const cardDemoCalendar = (c) => {
  const { tx, demos, onOpenChat } = c;
  return (
    <Card testId={c.tid('milli-dashboard-demo-calendar')} board={c.board}>
      <Label>{tx('demoCalendar')}</Label>
      <Rule color={c.accent} />
      {demos.length === 0 ? (
        <p className="text-[13px] text-[#8f8b83]">{tx('demosEmpty')}</p>
      ) : (
        <ul className="space-y-3">
          {demos.map((d) => {
            const dt = new Date(d.starts_at);
            return (
              <li key={d.id} className="flex items-start gap-3">
                <div className="w-[76px] shrink-0">
                  <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#b0aca3]">
                    {dt.toLocaleDateString(undefined, { month: 'short', day: '2-digit' })}
                  </div>
                  <div className="whitespace-nowrap text-[14px] font-bold text-[#EDEBE6]">{dt.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</div>
                </div>
                <div className="min-w-0">
                  <div className="truncate text-[14px] font-bold text-[#EDEBE6]">{d.company}</div>
                  <div className="text-[11px] text-[#8f8b83]">
                    {d.contact_name ? `${d.contact_name}, ` : ''}{tx('bookedBy', { who: d.booked_by || 'Milli' })}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <button
        type="button"
        onClick={onOpenChat}
        className="mt-4 flex w-full items-center justify-center gap-2 rounded-[10px] px-3 py-3 text-[12px] font-bold uppercase tracking-[0.12em] text-[#141210]"
        style={{ background: GOLD }}
        data-testid={c.tid('milli-dashboard-add-demo')}
      >
        <CalendarPlus size={14} /> {tx('addDemo')}
      </button>
    </Card>
  );
};

const cardActivation = (c) => {
  const { tx, act } = c;
  return (
    <Card testId={c.tid('milli-dashboard-activation')} board={c.board}>
      <Label>{tx('activation')}</Label>
      <Rule color={c.accent} />
      {(act.new_accounts || []).length === 0 ? (
        <p className="text-[13px] text-[#8f8b83]">{tx('activationEmpty')}</p>
      ) : (
        <ul className="space-y-3">
          {act.new_accounts.map((a, i) => {
            const age = daysAgo(a.at);
            return (
              <li key={i}>
                <div className="flex items-center justify-between">
                  <span className="text-[12px] font-bold uppercase tracking-[0.08em] text-[#EDEBE6]">{a.company || a.name || tx('unknown')}</span>
                  <span className="text-[11px] font-bold" style={{ color: a.has_demo ? GOLD : COPPER }}>
                    {a.has_demo ? tx('demoBookedShort') : tx('noDemoYet')}
                  </span>
                </div>
                <div className="mt-1 h-1 rounded-full bg-[#3a3c42]">
                  <div className="h-1 rounded-full" style={{ width: `${Math.min(100, ((age ?? 0) / 14) * 100)}%`, background: a.has_demo ? GOLD : COPPER }} />
                </div>
                <div className="mt-1 text-[11px] text-[#8f8b83]">{tx('signedUpDaysAgo', { count: age ?? 0 })}</div>
              </li>
            );
          })}
        </ul>
      )}
      <div className="mt-4 grid grid-cols-2 gap-3 border-t border-[#3a3c42] pt-3">
        <div>
          <Label>{tx('supportOpen')}</Label>
          <div className="text-[22px] font-extrabold" style={{ color: COPPER }}>{act.support_open ?? 0}</div>
        </div>
        <div>
          <Label>{tx('messagesWaiting')}</Label>
          <div className="text-[22px] font-extrabold" style={{ color: COPPER }}>{act.messages_waiting ?? 0}</div>
        </div>
      </div>
    </Card>
  );
};

const cardUnitCore = (c) => {
  const { tx, units } = c;
  return (
    <Card className={`${c.board ? '' : 'lg:col-span-2'} border-t-2`} testId={c.tid('unit-core')} board={c.board}>
      <div className="flex items-center justify-between">
        <img src={CORE4[0].src} alt={CORE4[0].name} className="h-8 w-auto" />
        <Chip color={GOLD}>{tx('unit.focus')}</Chip>
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3">
        <div><Label>{tx('unit.accounts')}</Label><div className="text-[26px] font-extrabold">{fmtNumber(units.core?.accounts) ?? '--'}</div></div>
        <div><Label>{tx('unit.signups30')}</Label><div className="text-[26px] font-extrabold">{fmtNumber(units.core?.signups_30d) ?? '--'}</div></div>
        <div><Label>{tx('unit.demosMonth')}</Label><div className="text-[26px] font-extrabold">{fmtNumber(units.core?.demos_month) ?? '--'}</div></div>
      </div>
    </Card>
  );
};

const cardUnitPq = (c) => {
  const { tx, units } = c;
  return (
    <Card testId={c.tid('unit-pq')} board={c.board}>
      <div className="flex items-center justify-between">
        <img src={CORE4[1].src} alt={CORE4[1].name} className="h-7 w-auto" />
        <Chip color={COPPER}>{tx('unit.engine')}</Chip>
      </div>
      <dl className="mt-4 space-y-1 text-[13px]">
        <div className="flex justify-between"><dt className="text-[#b0aca3]">{tx('unit.estimates30')}</dt><dd>{fmtNumber(units.pq?.estimates_30d) ?? '--'}</dd></div>
        <div className="flex justify-between"><dt className="text-[#b0aca3]">{tx('unit.research30')}</dt><dd>{fmtNumber(units.pq?.research_30d) ?? '--'}</dd></div>
      </dl>
    </Card>
  );
};

const cardUnitConstruction = (c) => {
  const { tx, units } = c;
  return (
    <Card testId={c.tid('unit-construction')} board={c.board}>
      <div className="flex items-center justify-between">
        <img src={CORE4[2].src} alt={CORE4[2].name} className="h-8 w-auto rounded-md" />
        <Chip>{tx('unit.provingGround')}</Chip>
      </div>
      <dl className="mt-4 space-y-1 text-[13px]">
        <div className="flex justify-between"><dt className="text-[#b0aca3]">{tx('unit.jobsActive')}</dt><dd>{fmtNumber(units.construction?.jobs_active) ?? '--'}</dd></div>
        <div className="flex justify-between"><dt className="text-[#b0aca3]">{tx('unit.photos7')}</dt><dd>{fmtNumber(units.construction?.photos_7d) ?? '--'}</dd></div>
        <div className="flex justify-between"><dt className="text-[#b0aca3]">{tx('unit.posts30')}</dt><dd>{fmtNumber(units.construction?.posts_30d) ?? '--'}</dd></div>
      </dl>
    </Card>
  );
};

const cardUnitFormquick = (c) => {
  const { tx, units } = c;
  return (
    <Card testId={c.tid('unit-formquick')} board={c.board}>
      <div className="flex items-center justify-between">
        <img src={FORMQUICK_LOCKUP} alt="FormQuick" className="h-9 w-auto rounded-md" onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'inline'; }} />
        <span className="text-[15px] font-extrabold" style={{ display: 'none' }}><span style={{ color: '#d6d2c8' }}>FORM</span><span style={{ color: PURPLE }}>QUICK</span></span>
        <Chip color={PURPLE}>{tx('unit.autopilot')}</Chip>
      </div>
      {units.formquick?.connected ? (
        <>
          <dl className="mt-4 space-y-1 text-[13px]" data-testid={c.tid('unit-formquick-numbers')}>
            <div className="flex justify-between"><dt className="text-[#b0aca3]">{tx('unit.mrr')}</dt><dd className="font-bold">{fmtMoney(units.formquick.mrr_cents, units.formquick.currency) ?? '--'}{units.formquick.goals?.mrr_cents != null && <span className="ml-1 text-[11px] font-normal text-[#8f8b83]" data-testid={c.tid('unit-formquick-goal-mrr')}>{tx('unit.goalOf', { n: fmtMoney(units.formquick.goals.mrr_cents, units.formquick.currency) })}</span>}</dd></div>
            <div className="flex justify-between"><dt className="text-[#b0aca3]">{tx('unit.paying')}</dt><dd>{fmtNumber(units.formquick.paid) ?? '--'}{units.formquick.goals?.paying != null && <span className="ml-1 text-[11px] text-[#8f8b83]" data-testid={c.tid('unit-formquick-goal-paying')}>{tx('unit.goalOf', { n: fmtNumber(units.formquick.goals.paying) })}</span>}</dd></div>
            {(units.formquick.trialing > 0 || units.formquick.past_due > 0) && (
              <div className="flex justify-between"><dt className="text-[#b0aca3]">{tx('unit.trialPastDue')}</dt><dd>{fmtNumber(units.formquick.trialing) ?? '0'} / <span style={{ color: units.formquick.past_due > 0 ? COPPER : undefined }}>{fmtNumber(units.formquick.past_due) ?? '0'}</span></dd></div>
            )}
            <div className="flex justify-between"><dt className="text-[#b0aca3]">{tx('unit.fqAccounts')}</dt><dd>{fmtNumber(units.formquick.accounts) ?? '--'}{units.formquick.signups_7d > 0 && <span className="ml-1 text-[11px]" style={{ color: PURPLE }}>+{units.formquick.signups_7d}</span>}</dd></div>
            <div className="flex justify-between"><dt className="text-[#b0aca3]">{tx('unit.submissionsMonth')}</dt><dd>{fmtNumber(units.formquick.submissions_month) ?? '--'}</dd></div>
            <div className="flex justify-between"><dt className="text-[#b0aca3]">{tx('unit.supportOpen')}</dt><dd data-testid={c.tid('unit-formquick-support')}><span style={{ color: units.formquick.support_open > 0 ? COPPER : undefined }}>{fmtNumber(units.formquick.support_open) ?? '--'}</span>{units.formquick.support_pending > 0 && <span className="ml-1 text-[11px] text-[#8f8b83]">{tx('unit.supportPending', { n: units.formquick.support_pending })}</span>}</dd></div>
          </dl>
          <p className="mt-3 text-[11px]" style={{ color: units.formquick.stale ? COPPER : '#8f8b83' }}>
            {tx('unit.asOf', { when: fmtWhen(units.formquick.as_of) })}{units.formquick.stripe_error ? ` ${tx('unit.stripeUnread')}` : ''}
          </p>
        </>
      ) : (
        <p className="mt-4 text-[12px] text-[#8f8b83]">{tx('unit.formquickWaiting')}</p>
      )}
    </Card>
  );
};

const cardNextMoves = (c) => {
  const { tx, moves, initiative, myMoves, search, onOpenInbox } = c;
  return (
    <Card testId={c.tid('milli-dashboard-next-moves')} board={c.board}>
      <Label>{tx('nextMoves')}</Label>
      <Rule color={c.accent} />
      {!moves?.now_move && !moves?.next_move && !moves?.later_move ? (
        <p className="text-[13px] text-[#8f8b83]">{tx('nextMovesEmpty')}</p>
      ) : (
        <dl className="space-y-4">
          {[['now', moves.now_move, GOLD], ['next', moves.next_move, MUTED], ['later', moves.later_move, '#8f8b83']].map(([k, text, color]) =>
            text ? (
              <div key={k} className="grid grid-cols-[56px_1fr] gap-3">
                <dt className="pt-1 text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color }}>{tx(`move.${k}`)}</dt>
                <dd className="text-[15px] leading-snug" style={{ color: k === 'later' ? MUTED : '#EDEBE6' }}>{text}</dd>
              </div>
            ) : null
          )}
        </dl>
      )}
      {moves?.written_at ? <div className="mt-4 text-[11px] text-[#8f8b83]">{tx('writtenBy', { when: fmtWhen(moves.written_at) })}</div> : null}

      {/* Milli's own moves today (090): what the initiative run did. */}
      <div className="mt-6 border-t border-[#3a3c42] pt-4" data-testid={c.tid('milli-dashboard-my-moves')}>
        <Label>{tx('myMoves')}</Label>
        {initiative?.settings && initiative.settings.enabled === false ? (
          <p className="mt-2 text-[12px] text-[#8f8b83]">{tx('myMovesOff')}</p>
        ) : initiative?.settings?.paused_until ? (
          <p className="mt-2 text-[12px] text-[#8f8b83]">{tx('myMovesPaused', { until: fmtWhen(initiative.settings.paused_until) })}</p>
        ) : myMoves.length === 0 ? (
          <p className="mt-2 text-[12px] text-[#8f8b83]">{tx('myMovesEmpty', { count: Number(initiative.yesterday_count) || 0 })}</p>
        ) : (
          <ul className="mt-2 space-y-2">
            {myMoves.map((m) => {
              const st = m.status === 'waiting_on_jo' ? 'waiting' : m.status === 'done' ? 'done' : m.status === 'failed' ? 'failed' : 'skipped';
              const color = st === 'done' ? GOLD : st === 'waiting' ? COPPER : '#8f8b83';
              const chip = <Chip color={color}>{tx(`moveStatus.${st}`)}</Chip>;
              return (
                <li key={m.id} className="flex items-start justify-between gap-3 text-[13px]">
                  <div className="min-w-0">
                    <div className="text-[#EDEBE6]">{m.title}</div>
                    {m.why ? <div className="text-[11px] text-[#8f8b83]">{m.why}</div> : null}
                  </div>
                  {st === 'waiting' && onOpenInbox ? (
                    <button type="button" onClick={onOpenInbox} className="shrink-0" aria-label={tx('moveStatus.waiting')}>{chip}</button>
                  ) : (
                    <div className="shrink-0">{chip}</div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="mt-6 border-t border-[#3a3c42] pt-4">
        <Label>{tx('searchTop')}</Label>
        {(search.sites || []).length > 1 && (
          <ul className="mt-2 space-y-1 text-[12px]" data-testid={c.tid('milli-dashboard-search-sites')}>
            {search.sites.map((s) => (
              <li key={s.site} className="flex justify-between gap-3">
                <span className="truncate text-[#b0aca3]">{String(s.site).replace(/^sc-domain:/, '')}</span>
                <span className="shrink-0 text-[#EDEBE6]">{fmtNumber(s.clicks) ?? 0} {tx('clicks')}</span>
              </li>
            ))}
          </ul>
        )}
        {(search.queries || []).length === 0 ? (
          <p className="mt-2 text-[12px] text-[#8f8b83]">{tx('searchEmpty')}</p>
        ) : (
          <ul className="mt-2 space-y-1 text-[13px]">
            {search.queries.map((q) => (
              <li key={q.query} className="flex justify-between gap-3">
                <span className="truncate text-[#EDEBE6]">{q.query}</span>
                <span className="shrink-0 text-[#b0aca3]">{q.clicks} {tx('clicks')} <span style={{ color: COPPER }}>#{Math.round(Number(q.position) || 0)}</span></span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
};

const cardActivity = (c) => {
  const { tx, activity, calls } = c;
  return (
    <Card testId={c.tid('milli-dashboard-activity')} board={c.board}>
      <div className="flex items-center justify-between">
        <Label>{tx('activity')}</Label>
        <span className="flex items-center gap-1 text-[11px] text-[#b0aca3]"><Phone size={11} /> {tx('callsWeek', { count: calls.count_7d ?? 0 })}</span>
      </div>
      <Rule color={c.accent} />
      {activity.length === 0 ? (
        <p className="text-[13px] text-[#8f8b83]">{tx('activityEmpty')}</p>
      ) : (
        <ul className="space-y-1.5 font-mono text-[12px]">
          {activity.map((row) => {
            const d = new Date(row.at);
            const time = Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
            const isPq = /pq/i.test(String(row.business || ''));
            return (
              <li key={`${row.kind}-${row.id}`} className="flex gap-3">
                <span className="shrink-0 text-[#8f8b83]">{time}</span>
                <span className="truncate" style={{ color: isPq ? COPPER : '#EDEBE6' }}>
                  [{String(row.kind).toUpperCase()}] {row.title}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
};

/* What My board can show. `labelKey` is the existing dashboard string for
 * the card; `wide` cards take two of the board's three columns on a desk. */
export const BOARD_CARDS = [
  { id: 'visitors', labelKey: 'visitors', render: cardVisitors },
  { id: 'signups', labelKey: 'signups', render: cardSignups },
  { id: 'demos', labelKey: 'demosBooked', render: cardDemos },
  { id: 'requests', labelKey: 'demoRequests', render: cardRequests },
  { id: 'mrr', labelKey: 'mrr', render: cardMrr },
  { id: 'trials', labelKey: 'trials', render: cardTrials },
  { id: 'channels', labelKey: 'channels', render: cardChannels },
  { id: 'demoCalendar', labelKey: 'demoCalendar', render: cardDemoCalendar },
  { id: 'activation', labelKey: 'activation', render: cardActivation },
  { id: 'unitCore', label: CORE4[0].name, render: cardUnitCore, wide: true },
  { id: 'unitPq', label: CORE4[1].name, render: cardUnitPq },
  { id: 'unitConstruction', label: CORE4[2].name, render: cardUnitConstruction },
  { id: 'unitFormquick', label: 'FormQuick.io', render: cardUnitFormquick },
  { id: 'nextMoves', labelKey: 'nextMoves', render: cardNextMoves, wide: true },
  { id: 'activity', labelKey: 'activity', render: cardActivity, wide: true },
];

export default function ConsoleDashboard({ t, onOpenChat, onOpenInbox, pendingCount = 0, refreshTick = 0, isDesktop = false }) {
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState([]);
  const [state, setState] = useState({ loading: true, error: null });
  // The ribbons' colors and brightness come from the board's saved settings
  // so they start right and follow the side bar live (ConsoleBoard).
  const [tubes, setTubes] = useState(() => readBoardSettings().tubes);
  const { user, profile } = useAuth() || {};
  const tx = (key, opts) => t(`milli.console.dashboard.${key}`, opts);
  // "owner only" is the server's refusal for a non-owner account. Name the
  // account instead of blaming the dashboard; the numbers are fine.
  const ownerOnly = /owner only/i.test(state.error || '');
  const signedInAs = profile?.full_name || user?.email || '';

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    const [dash, hist] = await Promise.allSettled([fetchDashboard(), fetchHistory({ limit: 8, newestFirst: true })]);
    if (dash.status === 'fulfilled') {
      setData(dash.value);
      setState({ loading: false, error: null });
    } else {
      setState({ loading: false, error: dash.reason?.message || 'load failed' });
    }
    if (hist.status === 'fulfilled') setActivity(hist.value.rows || []);
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshTick]);

  const f = data?.funnel || {};
  const pipe = data?.pipeline || {};
  const channels = (data?.channels || []).filter((c) => c.leads > 0 || c.posts > 0);
  const winner = channels.reduce((best, c) => (best == null || c.leads > best.leads ? c : best), null);
  const demos = data?.demos || [];
  const act = data?.activation || {};
  const units = data?.units || {};
  const moves = data?.next_moves || {};
  const initiative = data?.initiative || {};
  const trials = data?.trials || {};
  const trialRows = Array.isArray(trials.rows) ? trials.rows : [];
  const myMoves = Array.isArray(initiative.today) ? initiative.today : [];
  const search = data?.search || {};
  const calls = data?.calls || {};
  const pending = Number(data?.pending_approvals ?? pendingCount) || 0;

  // The fixed layout: rulebook colors, plain test ids.
  const ctx = {
    tx, f, channels, winner, demos, act, units, moves, initiative, trials, trialRows, myMoves, search, calls, activity,
    onOpenChat, onOpenInbox, board: false, accent: COPPER, tid: (id) => id,
  };

  return (
    <div className="relative min-h-full bg-[#1e2023] text-[#EDEBE6]" data-testid="milli-dashboard">
      {isDesktop ? <TubesBackground colors={tubes.colors} opacity={tubes.opacity} /> : null}
      {isDesktop ? (
        <img
          src={MILLI_WORDMARK}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-[100px] z-[1] hidden w-[720px] max-w-[60vw] -translate-x-1/2 select-none lg:block"
          style={{ opacity: 0.06 }}
        />
      ) : null}

      <div className="relative z-10 mx-auto max-w-[1440px] px-4 py-4 lg:px-8 lg:py-6">
        {/* Header row */}
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div>
            <Label>{tx('title')}</Label>
            <div className="text-[13px] text-[#b0aca3]">
              {data?.as_of ? tx('asOf', { when: fmtWhen(data.as_of) }) : state.loading ? '…' : ''}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {pending > 0 ? (
              <button
                type="button"
                onClick={onOpenInbox}
                className="rounded-[10px] px-3 py-2 text-[12px] font-bold uppercase tracking-[0.12em] text-[#141210]"
                style={{ background: GOLD }}
                data-testid="milli-dashboard-approvals"
              >
                {tx('approvalsWaiting', { count: pending })}
              </button>
            ) : null}
            <button
              type="button"
              onClick={onOpenChat}
              className="flex items-center gap-1.5 rounded-[10px] border border-[#6a6a6a] px-3 py-2 text-[12px] font-bold uppercase tracking-[0.12em] text-[#EDEBE6]"
              data-testid="milli-dashboard-chat"
            >
              <MessageSquare size={13} /> {tx('talk')}
            </button>
            <button type="button" onClick={load} className="rounded-[10px] p-2 text-[#b0aca3] hover:text-[#EDEBE6]" title={tx('refresh')} data-testid="milli-dashboard-refresh">
              <RefreshCw size={14} className={state.loading ? 'animate-spin' : ''} />
            </button>
          </div>
        </div>

        {state.error ? (
          <Card className="mb-4">
            <div className="text-sm text-red-300" data-testid="milli-dashboard-error">
              {ownerOnly ? tx('loadErrorOwner', { name: signedInAs }) : tx('loadError')}
            </div>
          </Card>
        ) : null}

        {/* Today strip: the funnel */}
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5" data-testid="milli-dashboard-funnel">
          {cardVisitors(ctx)}
          {cardSignups(ctx)}
          {cardDemos(ctx)}
          {cardRequests(ctx)}
          {cardMrr(ctx)}
        </div>

        {/* Prospects */}
        <div className="mt-8 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <h2 className="font-display text-[24px] tracking-[0.02em] text-[#EDEBE6]">{tx('prospects')}</h2>
          <Label>{tx('prospectsSub')}</Label>
        </div>
        <Rule />
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-5" data-testid="milli-dashboard-pipeline">
          {STAGES.map(([key, labelKey]) => {
            const rows = pipe[key] || [];
            const isPaying = key === 'paying';
            // Paying has no source until billing exists: null stays null so the
            // column says "Connect Stripe" instead of a made-up zero (091).
            const count = isPaying ? (pipe.paying_count ?? null) : (pipe[`${key}_count`] ?? rows.length);
            return (
              <div key={key} className="min-w-0">
                <div className="mb-2 flex items-center justify-between">
                  <Label>{tx(`stage.${labelKey}`)}</Label>
                  <span className="text-[13px] font-bold" style={{ color: isPaying ? GOLD : MUTED }}>{count ?? '--'}</span>
                </div>
                <div className="space-y-2">
                  {rows.length === 0 ? (
                    <div className="rounded-[14px] border border-dashed border-[#3a3c42] p-3 text-[12px] text-[#8f8b83]">{isPaying && count == null ? tx('connectStripe') : tx('stageEmpty')}</div>
                  ) : (
                    rows.map((r, i) => (
                      <div key={`${key}-${i}`} className={`rounded-[14px] border border-[#3a3c42] bg-[#2c2e33] p-3 ${isPaying ? 'opacity-90' : ''}`}>
                        <div className="truncate text-[14px] font-bold text-[#EDEBE6]">{r.company || r.name || tx('unknown')}</div>
                        <div className="truncate text-[11px] uppercase tracking-[0.1em] text-[#b0aca3]">
                          {[r.trade, r.contact_name || (r.company ? r.name : null), r.tier].filter(Boolean).join(', ')}
                        </div>
                        <div className="mt-2 flex items-center justify-between gap-2">
                          {r.source ? <Chip>{r.source}</Chip> : r.booked_by ? <Chip>{r.booked_by}</Chip> : <span />}
                          <span className="text-[11px] italic text-[#8f8b83]">{fmtWhen(r.at, { withTime: key === 'demo_booked' })}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Channels, demo calendar, activation */}
        <div className="mt-8 grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-4">
          {cardTrials(ctx)}
          {cardChannels(ctx)}
          {cardDemoCalendar(ctx)}
          {cardActivation(ctx)}
        </div>

        {/* Operating units */}
        <Label className="mt-8 mb-3">{tx('units')}</Label>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-5" data-testid="milli-dashboard-units">
          {cardUnitCore(ctx)}
          {cardUnitPq(ctx)}
          {cardUnitConstruction(ctx)}
          {cardUnitFormquick(ctx)}
        </div>

        {/* Next moves, search, activity */}
        <div className="mt-8 grid grid-cols-1 gap-3 lg:grid-cols-2">
          {cardNextMoves(ctx)}
          {cardActivity(ctx)}
        </div>

        {/* Core 4 band */}
        <div className="mt-10 rounded-[14px] border border-[#3a3c42] bg-[#25272B] px-4 py-6" data-testid="milli-dashboard-core4">
          <div className="grid grid-cols-2 items-center gap-6 lg:grid-cols-4">
            {CORE4.map((b, i) => (
              <div key={b.key} className={`flex items-center justify-center ${i > 0 ? 'lg:border-l lg:border-[#4a4c52]' : ''}`}>
                <img src={b.src} alt={b.name} className={`h-7 w-auto lg:h-10 ${b.plate ? 'rounded-md' : ''}`} />
              </div>
            ))}
          </div>
        </div>

        {/* My board: J.O.'s open space. Clear by default so the ribbons show
            through and follow the mouse; the cards he picks float on it. */}
        <ConsoleBoard ctx={ctx} cards={BOARD_CARDS} tx={tx} isDesktop={isDesktop} onTubesChange={setTubes} />

        <div className="mt-3 pb-4 text-[11px] uppercase tracking-[0.14em] text-[#8f8b83]">K.O. CO+RE Systems {new Date().getFullYear()}</div>
      </div>
    </div>
  );
}
